import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { FULL_MONTHS, qaStatusColor, qaStatus, classNames, thisMonth, thisYear } from "../lib/utils";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  ReferenceDot,
} from "recharts";
import { FileText, AlertTriangle, UserCircle2 } from "lucide-react";

const CHART_MARGIN = { top: 10, right: 10, left: -10, bottom: 0 };
const PCT_DOMAIN = [0, 100];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const LINE_DOT = { r: 4, fill: "#0369a1" };
const SELECTED_DOT_FILL = "#dc2626";

// Default static years if the DB has none yet
const DEFAULT_YEAR_OPTIONS = [2025, 2026, 2027, 2028];

export default function QADashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [homes, setHomes] = useState([]);
  const [home, setHome] = useState(user.access_role === "House Manager" ? user.assigned_home : "ALL");
  const [year, setYear] = useState(thisYear());
  const [month, setMonth] = useState(thisMonth());

  useEffect(() => {
    api.get("/homes").then(({ data }) => setHomes(data));
  }, []);

  useEffect(() => {
    api.get("/qa/dashboard", { params: { home, year, month } }).then(({ data }) => setData(data));
  }, [home, year, month]);

  // Year dropdown options: union of defaults + audit-data years, descending
  const yearOptions = useMemo(() => {
    const set = new Set([...DEFAULT_YEAR_OPTIONS, year]);
    (data?.available_years || []).forEach((y) => set.add(y));
    return Array.from(set).sort((a, b) => b - a);
  }, [data, year]);

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;

  const trend = data.trend.map((t) => ({
    month: FULL_MONTHS[t.month - 1].slice(0, 3),
    monthIdx: t.month,
    score: t.score,
  }));
  const homeManager = data.house_manager;

  // The dashboard now binds to the SELECTED audit (home + month + year).
  // If no audit exists for that exact slot → show a clear empty-state.
  const selected = data.selected;
  const monthLabel = FULL_MONTHS[month - 1];

  let weakest = null;
  if (selected?.category_scores?.length) {
    weakest = selected.category_scores.reduce((a, b) => (a.score < b.score ? a : b));
  }

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">QA / Home Metrics</p>
          <h1 className="text-h1 mt-1">QA Dashboard</h1>
          <div className="text-sm text-slate-600 mt-1 space-y-0.5">
            <p>{home === "ALL" ? "All Homes" : home} · {monthLabel} {year}</p>
            {home !== "ALL" && (
              <p className="inline-flex items-center gap-1.5" data-testid="qa-dashboard-home-manager">
                <UserCircle2 size={13} className="text-slate-400" />
                House Manager:&nbsp;
                {homeManager?.manager_name ? (
                  <>
                    <span className="font-medium text-slate-800">{homeManager.manager_name}</span>
                    {homeManager.manager_active === false && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0 text-[11.5px] font-semibold">
                        Inactive
                      </span>
                    )}
                  </>
                ) : (
                  <span className="italic text-slate-500">Unassigned</span>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="qa-dashboard-filters">
          {user.access_role !== "House Manager" && (
            <select value={home} onChange={(e) => setHome(e.target.value)}
                    className="text-sm rounded-md border border-slate-200 bg-white px-3 py-2"
                    data-testid="qa-dashboard-home-select">
              <option value="ALL">All Homes</option>
              {homes.map((h) => <option key={h.home_name} value={h.home_name}>{h.home_name}</option>)}
            </select>
          )}
          <select value={month} onChange={(e) => setMonth(+e.target.value)}
                  className="text-sm rounded-md border border-slate-200 bg-white px-3 py-2"
                  data-testid="qa-dashboard-month-select">
            {FULL_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(+e.target.value)}
                  className="text-sm rounded-md border border-slate-200 bg-white px-3 py-2 w-24"
                  data-testid="qa-dashboard-year-select">
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {(!selected || home === "ALL") ? (
        home === "ALL" ? (
          <div className="nv-card p-10 text-center text-slate-500" data-testid="qa-dashboard-empty">
            <AlertTriangle size={28} className="mx-auto text-slate-300 mb-2" />
            Select a single home to view QA Audit data for {monthLabel} {year}.
          </div>
        ) : (
          <div className="nv-card p-10 text-center text-slate-500" data-testid="qa-dashboard-empty">
            <AlertTriangle size={28} className="mx-auto text-slate-300 mb-2" />
            No QA Audit data found for <strong>{home}</strong>, <strong>{monthLabel}</strong>, <strong>{year}</strong>.
            <div className="mt-3">
              <Link to={`/qa/entry?home=${encodeURIComponent(home)}&month=${month}&year=${year}`} className="text-sm text-sky-700 font-semibold hover:underline">
                Start a new QA audit →
              </Link>
            </div>
          </div>
        )
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="nv-card p-5" data-testid="qa-dashboard-overall-card">
              <p className="text-small-caps">Overall QA Score</p>
              <p className="mt-2 text-4xl font-heading font-bold text-slate-900 tabular-nums">{selected.overall_score_percent}%</p>
              <span className={classNames("mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border", qaStatusColor(selected.overall_status))}>{selected.overall_status}</span>
              <p className="text-xs text-slate-500 mt-2">{monthLabel} {year} · audit by {selected.completed_by}</p>
            </div>
            <div className="nv-card p-5" data-testid="qa-dashboard-weakest-card">
              <p className="text-small-caps">Weakest Category</p>
              <p className="mt-2 text-lg font-heading font-bold text-slate-900">{weakest?.category_name || "—"}</p>
              <p className="text-3xl font-heading font-bold text-rose-600 tabular-nums mt-1">{weakest?.score?.toFixed(1) ?? "0"}%</p>
            </div>
            <div className="nv-card p-5 flex flex-col">
              <p className="text-small-caps">QA Audit Report</p>
              <p className="mt-2 text-sm text-slate-600 flex-1">Print-ready confidential audit report for {monthLabel} {year}.</p>
              <Link
                to={`/qa/report?home=${encodeURIComponent(home)}&month=${month}&year=${year}`}
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:underline"
                data-testid="qa-view-report"
              >
                <FileText size={14} /> View Report
              </Link>
            </div>
          </div>

          {selected.manager_notes && (
            <div className="nv-card p-5" data-testid="qa-dashboard-notes">
              <p className="text-small-caps">Manager Notes / Action Items</p>
              <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{selected.manager_notes}</p>
            </div>
          )}

          <div className="nv-card p-5">
            <h3 className="text-h3">Month-over-Month Trend · {year}</h3>
            <p className="text-xs text-slate-500 mt-0.5">Selected month highlighted in red</p>
            <div className="mt-4 h-56 min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={50}>
                <LineChart data={trend} margin={CHART_MARGIN}>
                  <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} stroke="#64748b" />
                  <YAxis domain={PCT_DOMAIN} tickLine={false} axisLine={false} fontSize={12} stroke="#64748b" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="score" stroke="#0369a1" strokeWidth={2.5} dot={LINE_DOT} />
                  {trend.find((t) => t.monthIdx === month) && (
                    <ReferenceDot
                      x={FULL_MONTHS[month - 1].slice(0, 3)}
                      y={trend.find((t) => t.monthIdx === month).score}
                      r={6}
                      fill={SELECTED_DOT_FILL}
                      stroke="#fff"
                      strokeWidth={2}
                      isFront
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="nv-card overflow-x-auto" data-testid="qa-dashboard-categories">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-h3">Category Overview · {monthLabel} {year}</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 text-left">Category</th>
                  <th className="px-3 py-3 text-right">Score</th>
                  <th className="px-3 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {selected.category_scores.map((c) => {
                  const status = qaStatus(c.score);
                  return (
                    <tr key={c.category_id} className="border-t border-slate-100">
                      <td className="px-5 py-3 font-medium text-slate-800">{c.category_name}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold">{c.score?.toFixed(1)}%</td>
                      <td className="px-3 py-3"><span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border", qaStatusColor(status))}>{status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
